import { format, isValid, parseISO } from 'date-fns';

import { FetchTimelineDataParams } from './services';
import {
  EMPTY_TIMELINE_FILTERS,
  TimelineFilters,
  TimelineSortOption,
  TimelineTab,
} from './timelineData';

export const TIMELINE_URL_PARAMS = {
  tab: 'timeline.tab',
  page: 'timeline.page',
  pageSize: 'timeline.pageSize',
  search: 'timeline.search',
  sort: 'timeline.sort',
  dateStart: 'timeline.dateStart',
  dateEnd: 'timeline.dateEnd',
  favorites: 'timeline.favorites',
} as const;

type TimelineArrayFilter = {
  [Filter in keyof TimelineFilters]: TimelineFilters[Filter] extends string[]
    ? Filter extends keyof FetchTimelineDataParams
      ? Filter
      : never
    : never;
}[keyof TimelineFilters];

const URL_ARRAY_FILTERS: Array<[string, TimelineArrayFilter]> = [
  ['timeline.markets', 'markets'],
  ['timeline.categories', 'categories'],
  ['timeline.types', 'types'],
  ['timeline.species', 'species'],
  ['timeline.themes', 'themes'],
  ['timeline.relevances', 'relevances'],
];

const URL_STATE_PARAMS = [
  ...Object.values(TIMELINE_URL_PARAMS),
  ...URL_ARRAY_FILTERS.map(([param]) => param),
];

const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_TAB: TimelineTab = 'all';

export interface TimelineUrlState {
  activeTab: TimelineTab;
  page: number;
  pageSize: number;
  search: string;
  // null: o usuário não escolheu; cada aba aplica o seu padrão.
  sort: TimelineSortOption | null;
  filters: TimelineFilters;
}

const isTimelineTab = (value: string | null): value is TimelineTab =>
  value === 'highlights' || value === 'future' || value === 'all';

const isTimelineSort = (value: string | null): value is TimelineSortOption =>
  value === 'materiality' || value === 'recent' || value === 'oldest';

const parsePositiveInteger = (value: string | null, fallback: number) => {
  if (!value || !/^[1-9]\d*$/.test(value)) return fallback;

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : fallback;
};

const getArrayParam = (params: URLSearchParams, key: string) => [
  ...new Set(
    params
      .getAll(key)
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean),
  ),
];

const isCivilDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const date = parseISO(value);
  return isValid(date) && format(date, 'yyyy-MM-dd') === value;
};

const uniqueFilterValues = (values: string[]) => [
  ...new Set(values.map((value) => value.trim()).filter(Boolean)),
];

export const parseTimelineUrlState = (
  search: string,
  operationScoped = false,
): TimelineUrlState => {
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  const pageSize = parsePositiveInteger(
    params.get(TIMELINE_URL_PARAMS.pageSize),
    DEFAULT_PAGE_SIZE,
  );
  const dateStartParam = params.get(TIMELINE_URL_PARAMS.dateStart) ?? '';
  const dateEndParam = params.get(TIMELINE_URL_PARAMS.dateEnd) ?? '';
  let dateStart = isCivilDate(dateStartParam) ? dateStartParam : '';
  let dateEnd = isCivilDate(dateEndParam) ? dateEndParam : '';

  if (dateStart && dateEnd && dateStart > dateEnd) {
    dateStart = '';
    dateEnd = '';
  }

  const filters: TimelineFilters = {
    ...EMPTY_TIMELINE_FILTERS,
    dateStart,
    dateEnd,
    favorites: params.get(TIMELINE_URL_PARAMS.favorites) === 'true',
  };

  URL_ARRAY_FILTERS.forEach(([param, filter]) => {
    if (operationScoped && filter === 'markets') return;
    filters[filter] = getArrayParam(params, param);
  });
  if (operationScoped) filters.favorites = false;

  return {
    activeTab: isTimelineTab(params.get(TIMELINE_URL_PARAMS.tab))
      ? (params.get(TIMELINE_URL_PARAMS.tab) as TimelineTab)
      : DEFAULT_TAB,
    page: parsePositiveInteger(params.get(TIMELINE_URL_PARAMS.page), 1),
    pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    search: params.get(TIMELINE_URL_PARAMS.search) ?? '',
    sort: isTimelineSort(params.get(TIMELINE_URL_PARAMS.sort))
      ? (params.get(TIMELINE_URL_PARAMS.sort) as TimelineSortOption)
      : null,
    filters,
  };
};

export const serializeTimelineUrlState = (
  currentSearch: string,
  state: TimelineUrlState,
  operationScoped = false,
) => {
  const params = new URLSearchParams(currentSearch.replace(/^\?/, ''));

  URL_STATE_PARAMS.forEach((param) => params.delete(param));

  if (state.activeTab !== DEFAULT_TAB) {
    params.set(TIMELINE_URL_PARAMS.tab, state.activeTab);
  }
  if (Number.isSafeInteger(state.page) && state.page > 1) {
    params.set(TIMELINE_URL_PARAMS.page, String(state.page));
  }
  if (
    PAGE_SIZES.includes(state.pageSize) &&
    state.pageSize !== DEFAULT_PAGE_SIZE
  ) {
    params.set(TIMELINE_URL_PARAMS.pageSize, String(state.pageSize));
  }
  if (state.search.trim()) {
    params.set(TIMELINE_URL_PARAMS.search, state.search);
  }
  if (state.sort) {
    params.set(TIMELINE_URL_PARAMS.sort, state.sort);
  }

  URL_ARRAY_FILTERS.forEach(([param, filter]) => {
    if (operationScoped && filter === 'markets') return;
    uniqueFilterValues(state.filters[filter]).forEach((value) =>
      params.append(param, value),
    );
  });

  const validStart = isCivilDate(state.filters.dateStart)
    ? state.filters.dateStart
    : '';
  const validEnd = isCivilDate(state.filters.dateEnd)
    ? state.filters.dateEnd
    : '';
  const reversedDateRange = Boolean(
    validStart && validEnd && validStart > validEnd,
  );

  if (!reversedDateRange && validStart) {
    params.set(TIMELINE_URL_PARAMS.dateStart, validStart);
  }
  if (!reversedDateRange && validEnd) {
    params.set(TIMELINE_URL_PARAMS.dateEnd, validEnd);
  }
  if (!operationScoped && state.filters.favorites) {
    params.set(TIMELINE_URL_PARAMS.favorites, 'true');
  }

  return params.toString();
};
